import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { CATEGORIES, PRODUCTS } from "../data/products";
import ProductCard from "../components/ProductCard";

export default function Catalog() {
  const { category: catSlug } = useParams();
  const { t, lang } = useI18n();

  const currentCategory = useMemo(
    () => CATEGORIES.find((c) => c.slug === catSlug),
    [catSlug]
  );

  const products = useMemo(() => {
    if (catSlug) return PRODUCTS.filter((p) => p.category === catSlug);
    return PRODUCTS;
  }, [catSlug]);

  return (
    <div data-testid="catalog-page" className="min-h-screen">
      {/* Page header */}
      <section className="relative py-20 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] to-[#050505]" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <nav className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-5">
            <Link to="/" className="hover:text-[#39FF14]">
              {t.nav.home}
            </Link>
            <span className="mx-2">/</span>
            <Link to="/produits" className="hover:text-[#39FF14]">
              {t.nav.products}
            </Link>
            {currentCategory && (
              <>
                <span className="mx-2">/</span>
                <span className="text-[#39FF14]">{t.cat[currentCategory.key].name}</span>
              </>
            )}
          </nav>
          <h1 className="font-display text-5xl sm:text-6xl font-bold uppercase tracking-tight">
            {currentCategory ? (
              <>
                <span className="chrome-text">{t.cat[currentCategory.key].name}</span>
              </>
            ) : (
              <span className="chrome-text">{t.products.title}</span>
            )}
          </h1>
          <p className="mt-5 text-zinc-400 font-body max-w-2xl">
            {currentCategory ? t.cat[currentCategory.key].desc : t.products.subtitle}
          </p>
        </div>
      </section>

      {/* Layout with sidebar */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[260px_1fr] gap-12">
        {/* Sidebar */}
        <aside className="space-y-8" data-testid="catalog-sidebar">
          <div>
            <div className="label-tech mb-4">{t.products.filter}</div>
            <ul className="space-y-1">
              <li>
                <Link
                  to="/produits"
                  data-testid="filter-all"
                  className={`block py-2 px-3 font-display uppercase tracking-widest text-sm border-l-2 transition-all ${
                    !catSlug
                      ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/5"
                      : "border-transparent text-zinc-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  {t.products.all}
                </Link>
              </li>
              {CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/produits/${c.slug}`}
                    data-testid={`filter-${c.slug}`}
                    className={`block py-2 px-3 font-display uppercase tracking-widest text-sm border-l-2 transition-all ${
                      catSlug === c.slug
                        ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/5"
                        : "border-transparent text-zinc-400 hover:text-white hover:border-white/20"
                    }`}
                  >
                    {t.cat[c.key].name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {currentCategory && (
            <div>
              <div className="label-tech mb-4">{t.products.subcategories}</div>
              <ul className="space-y-2">
                {currentCategory.subcategories.map((sub) => (
                  <li key={sub.slug} className="text-sm text-zinc-400 font-body">
                    <span className="inline-block w-2 h-2 bg-[#39FF14] mr-2 align-middle" />
                    {sub[lang] || sub.fr}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Products grid */}
        <div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
            data-testid="catalog-grid"
          >
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
